import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Filter, CheckCircle2, Store, User2 } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/datetime";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtFecha = (iso) => fmtDateTime(iso);

export default function AuditoriaFactorRiesgo() {
  const [items, setItems] = useState([]);
  const [totalPendiente, setTotalPendiente] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [sucFiltro, setSucFiltro] = useState("all");
  const [rango, setRango] = useState({ desde: "", hasta: "" });
  const [liquidando, setLiquidando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sucFiltro !== "all") params.set("id_sucursal", sucFiltro);
      if (rango.desde && rango.hasta) { params.set("desde", rango.desde); params.set("hasta", rango.hasta); }
      const qs = params.toString();
      const { data } = await api.get(`/operativa/valet-nocobro${qs ? `?${qs}` : ""}`);
      setItems(data.items || []);
      setTotalPendiente(data.total_pendiente || 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo cargar la auditoría");
    } finally { setLoading(false); }
  }, [sucFiltro, rango]);

  useEffect(() => {
    listSucursales().then(({ data }) => setSucursales(data || [])).catch(() => {});
  }, []);
  useEffect(() => { cargar(); }, [sucFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const liquidar = async (idCaja) => {
    setLiquidando(idCaja);
    try {
      await api.patch(`/operativa/valet-nocobro/${idCaja}/liquidar`);
      toast.success("Factor de Riesgo marcado como liquidado");
      await cargar();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLiquidando(null); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-5 lg:p-8" data-testid="auditoria-factor-page">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-[#047857]" /> Auditoría Factor de Riesgo
          </h1>
          <p className="text-sm text-[#52525B] mt-1">Valet Sin Costo / Eventos — folios consumidos por encargado y monto a cobrar del Factor de Riesgo.</p>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div className="w-full sm:w-64">
            <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Sucursal / Evento</Label>
            <Select value={sucFiltro} onValueChange={setSucFiltro}>
              <SelectTrigger className="h-10 rounded-none border-[#0A0A0A] mt-1" data-testid="auditoria-suc-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las sucursales</SelectItem>
                {sucursales.map((s) => <SelectItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Desde</Label>
            <Input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} data-testid="auditoria-desde" className="h-10 rounded-none border-[#0A0A0A] mt-1 w-[160px]" />
          </div>
          <div>
            <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Hasta</Label>
            <Input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} data-testid="auditoria-hasta" className="h-10 rounded-none border-[#0A0A0A] mt-1 w-[160px]" />
          </div>
          <button onClick={cargar} data-testid="auditoria-aplicar" className="h-10 px-4 border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs inline-flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200">
            <Filter className="h-3.5 w-3.5" /> Aplicar
          </button>
          <div className="ml-auto text-right">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Total pendiente por cobrar</p>
            <p className="font-display font-black text-xl text-[#B45309]" data-testid="auditoria-total-pendiente">{money(totalPendiente)}</p>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#047857]" /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#d4d4d8] py-16 text-center text-[#52525B]" data-testid="auditoria-empty">No hay eventos de Valet sin costo registrados en este rango.</div>
        ) : (
          <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
            <table className="w-full text-sm" data-testid="auditoria-tabla">
              <thead><tr className="border-b border-[#e4e4e7] text-left">
                <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Fecha/Hora</th>
                <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Sucursal/Evento</th>
                <th className="px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Empleado</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Usados</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Cancel.</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Netos</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Factor U.</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Total a cobrar</th>
                <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Estatus</th>
              </tr></thead>
              <tbody>
                {items.map((it) => (
                  <motion.tr key={it.id_caja} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="border-b border-[#f0f0f1]" data-testid={`auditoria-row-${it.id_caja}`}>
                    <td className="px-3 py-3 whitespace-nowrap">{fmtFecha(it.fecha_cierre)}</td>
                    <td className="px-3 py-3"><span className="inline-flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-[#71717A]" />{it.sucursal_nombre || "—"}</span></td>
                    <td className="px-3 py-3"><span className="inline-flex items-center gap-1.5"><User2 className="h-3.5 w-3.5 text-[#71717A]" />{it.operador || "—"}</span></td>
                    <td className="px-3 py-3 text-right font-mono">{it.folios_totales}</td>
                    <td className="px-3 py-3 text-right font-mono">{it.folios_cancelados_cortesia}</td>
                    <td className="px-3 py-3 text-right font-mono font-bold">{it.folios_efectivos}</td>
                    <td className="px-3 py-3 text-right font-mono">{money(it.cuota_factor_riesgo)}</td>
                    <td className="px-3 py-3 text-right font-mono font-black text-[#047857]" data-testid={`auditoria-total-${it.id_caja}`}>{money(it.total_factor_riesgo)}</td>
                    <td className="px-3 py-3 text-center">
                      {it.estatus_cobro_empleado === "LIQUIDADO" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#047857] border border-[#10B981]/40 bg-[#ECFDF5] px-2 py-1" data-testid={`auditoria-estatus-${it.id_caja}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Liquidado
                        </span>
                      ) : (
                        <button onClick={() => liquidar(it.id_caja)} disabled={liquidando === it.id_caja}
                          data-testid={`auditoria-liquidar-${it.id_caja}`}
                          className="inline-flex items-center gap-1.5 h-8 px-3 border border-[#0A0A0A] text-[11px] font-bold uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 disabled:opacity-50">
                          {liquidando === it.id_caja ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Marcar como Liquidado
                        </button>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
